import { useState, useEffect, useRef, useCallback } from 'react';
import { X, ShoppingCart, Zap, Shield, Brain, TrendingUp, Star, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

interface Employee {
  id: number;
  name: string;
  roleTitle: string;
  departmentId: string;
  bio?: string;
  skills?: string[];
  price?: number;
  photoUrl?: string;
  compatibleHarnesses?: string[];
  valueProposition?: string;
  availabilityStatus?: string;
}

interface HoloDossierProps {
  employee: Employee | null;
  onClose: () => void;
  onAddToCart: (emp: Employee) => void;
}

const DEPT_COLORS: Record<string, string> = {
  'executive-operations': '#00A6EB',
  'sales-marketing': '#FF0055',
  'finance-accounting': '#FFB800',
  'technology-development': '#00FF88',
  'hr-talent': '#A855F7',
  'customer-success': '#FF6B00',
  'content-creative': '#FF1493',
  'data-analytics': '#00FFFF',
  'ecommerce-amazon': '#FF8C00',
  'legal-compliance': '#7CFC00',
};

const DEPT_NAMES: Record<string, string> = {
  'executive-operations': 'Executive Operations',
  'sales-marketing': 'Sales & Marketing',
  'finance-accounting': 'Finance & Accounting',
  'technology-development': 'Technology & Development',
  'hr-talent': 'HR & Talent',
  'customer-success': 'Customer Success',
  'content-creative': 'Content & Creative',
  'data-analytics': 'Data & Analytics',
  'ecommerce-amazon': 'E-Commerce & Amazon',
  'legal-compliance': 'Legal & Compliance',
};

// Radar chart drawn on canvas
function RadarChart({ skills, color }: { skills: string[]; color: string }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef<number>(0);
  const progressRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const W = canvas.width;
    const H = canvas.height;
    const cx = W / 2;
    const cy = H / 2;
    const maxR = Math.min(W, H) * 0.38;
    const n = Math.min(skills.length, 8);
    if (n < 3) return;

    // Generate pseudo-random values for each skill (deterministic based on skill name)
    const values = skills.slice(0, n).map(s => {
      let hash = 0;
      for (let i = 0; i < s.length; i++) hash = (hash * 31 + s.charCodeAt(i)) & 0xffffffff;
      return 0.55 + (Math.abs(hash) % 45) / 100;
    });

    const draw = (progress: number) => {
      ctx.clearRect(0, 0, W, H);

      // Grid rings
      for (let ring = 1; ring <= 4; ring++) {
        const r = (ring / 4) * maxR;
        ctx.beginPath();
        for (let i = 0; i < n; i++) {
          const angle = (i / n) * Math.PI * 2 - Math.PI / 2;
          const x = cx + Math.cos(angle) * r;
          const y = cy + Math.sin(angle) * r;
          if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
        }
        ctx.closePath();
        ctx.strokeStyle = `rgba(${hexToRgb(color)}, ${ring === 4 ? 0.3 : 0.1})`;
        ctx.lineWidth = ring === 4 ? 1 : 0.5;
        ctx.stroke();
      }

      // Axis lines
      for (let i = 0; i < n; i++) {
        const angle = (i / n) * Math.PI * 2 - Math.PI / 2;
        ctx.beginPath();
        ctx.moveTo(cx, cy);
        ctx.lineTo(cx + Math.cos(angle) * maxR, cy + Math.sin(angle) * maxR);
        ctx.strokeStyle = `rgba(${hexToRgb(color)}, 0.15)`;
        ctx.lineWidth = 0.5;
        ctx.stroke();
      }

      // Data polygon
      ctx.beginPath();
      for (let i = 0; i < n; i++) {
        const angle = (i / n) * Math.PI * 2 - Math.PI / 2;
        const r = values[i] * maxR * progress;
        const x = cx + Math.cos(angle) * r;
        const y = cy + Math.sin(angle) * r;
        if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
      }
      ctx.closePath();
      ctx.fillStyle = `rgba(${hexToRgb(color)}, 0.15)`;
      ctx.fill();
      ctx.strokeStyle = color;
      ctx.lineWidth = 1.5;
      ctx.stroke();

      // Data points
      for (let i = 0; i < n; i++) {
        const angle = (i / n) * Math.PI * 2 - Math.PI / 2;
        const r = values[i] * maxR * progress;
        const x = cx + Math.cos(angle) * r;
        const y = cy + Math.sin(angle) * r;
        ctx.beginPath();
        ctx.arc(x, y, 3, 0, Math.PI * 2);
        ctx.fillStyle = color;
        ctx.fill();
        ctx.beginPath();
        ctx.arc(x, y, 5, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${hexToRgb(color)}, 0.3)`;
        ctx.fill();
      }

      // Skill labels
      ctx.font = '9px "Share Tech Mono", monospace';
      ctx.fillStyle = `rgba(232,244,255,0.7)`;
      ctx.textAlign = 'center';
      for (let i = 0; i < n; i++) {
        const angle = (i / n) * Math.PI * 2 - Math.PI / 2;
        const labelR = maxR + 18;
        const x = cx + Math.cos(angle) * labelR;
        const y = cy + Math.sin(angle) * labelR + 3;
        const label = skills[i].length > 10 ? skills[i].slice(0, 9) + '…' : skills[i];
        ctx.fillText(label.toUpperCase(), x, y);
      }
    };

    // Animate in
    const startTime = performance.now();
    const duration = 800;
    const animate = (now: number) => {
      const elapsed = now - startTime;
      progressRef.current = Math.min(elapsed / duration, 1);
      draw(progressRef.current);
      if (progressRef.current < 1) {
        animRef.current = requestAnimationFrame(animate);
      }
    };
    animRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animRef.current);
  }, [skills, color]);

  return <canvas ref={canvasRef} width={240} height={240} style={{ display: 'block' }} />;
}

function hexToRgb(hex: string): string {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return '0,166,235';
  return `${parseInt(result[1], 16)},${parseInt(result[2], 16)},${parseInt(result[3], 16)}`;
}

export default function HoloDossier({ employee, onClose, onAddToCart }: HoloDossierProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [scanProgress, setScanProgress] = useState(0);
  const [tiltX, setTiltX] = useState(0);
  const [tiltY, setTiltY] = useState(0);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (employee) {
      setIsVisible(false);
      setScanProgress(0);
      requestAnimationFrame(() => {
        setIsVisible(true);
        // Animate scan progress
        let p = 0;
        const interval = setInterval(() => {
          p += 2;
          setScanProgress(Math.min(p, 100));
          if (p >= 100) clearInterval(interval);
        }, 20);
        return () => clearInterval(interval);
      });
    }
  }, [employee]);

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const panel = panelRef.current;
    if (!panel) return;
    const rect = panel.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    setTiltX((y - 0.5) * -4);
    setTiltY((x - 0.5) * 4);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setTiltX(0);
    setTiltY(0);
  }, []);

  if (!employee) return null;

  const color = DEPT_COLORS[employee.departmentId] || '#00A6EB';
  const deptName = DEPT_NAMES[employee.departmentId] || employee.departmentId;
  const price = employee.price ?? 297;
  const initials = employee.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
  const skills = employee.skills || ['Communication', 'Analysis', 'Strategy', 'Automation', 'Reporting', 'Planning'];
  const harnesses = employee.compatibleHarnesses || ['ChatGPT', 'Claude', 'Gemini', 'Manus', 'Grok'];

  const handleAddToCart = () => {
    onAddToCart(employee);
    toast.success(`${employee.name} added to Data Core`, {
      description: `$${price.toLocaleString()} deployment package`,
      style: { background: '#0D1117', border: '1px solid #00A6EB', color: '#E8F4FF' },
    });
  };

  return (
    <div
      className="dossier-overlay"
      onClick={(e) => { if (e.currentTarget === e.target) onClose(); }}
      style={{
        opacity: isVisible ? 1 : 0,
        transition: 'opacity 0.3s ease',
      }}
    >
      <div
        ref={panelRef}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        className="dossier-panel"
        style={{
          transform: isVisible
            ? `perspective(1200px) rotateX(${tiltX}deg) rotateY(${tiltY}deg) scale(1)`
            : 'perspective(1200px) scale(0.92) translateY(20px)',
          transition: 'transform 0.4s cubic-bezier(0.23,1,0.32,1)',
          padding: '2rem',
          maxWidth: 900,
          width: '95vw',
          maxHeight: '90vh',
          overflowY: 'auto',
          position: 'relative',
        }}
      >
        {/* Corner decorations */}
        <div className="dossier-corner dossier-corner-tl" />
        <div className="dossier-corner dossier-corner-tr" />
        <div className="dossier-corner dossier-corner-bl" />
        <div className="dossier-corner dossier-corner-br" />

        {/* Scan progress bar */}
        <div style={{
          position: 'absolute', top: 0, left: 0,
          height: 2,
          width: `${scanProgress}%`,
          background: `linear-gradient(90deg, ${color}, rgba(${hexToRgb(color)},0.3))`,
          transition: 'width 0.05s linear',
          zIndex: 10,
        }} />

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <div style={{
              fontFamily: 'Share Tech Mono, monospace',
              fontSize: '0.55rem',
              letterSpacing: '0.3em',
              color: color,
              textTransform: 'uppercase',
              textShadow: `0 0 8px ${color}`,
            }}>
              ◈ CLASSIFIED DOSSIER — AGENT PROFILE
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'rgba(255,0,85,0.1)',
              border: '1px solid rgba(255,0,85,0.4)',
              borderRadius: 4,
              padding: '4px 10px',
              cursor: 'pointer',
              color: '#FF0055',
              fontFamily: 'Orbitron, monospace',
              fontSize: '0.55rem',
              fontWeight: 700,
              letterSpacing: '0.2em',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              transition: 'all 0.15s ease',
            }}
            onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(255,0,85,0.2)'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(255,0,85,0.1)'; }}
          >
            <X size={10} /> CLOSE
          </button>
        </div>

        {/* Main content grid */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '2rem' }}>
          {/* Left column: avatar + radar */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', alignItems: 'center' }}>
            {/* Avatar */}
            <div style={{
              width: 140, height: 140,
              background: employee.photoUrl
                ? `url(${employee.photoUrl}) center/cover`
                : `linear-gradient(135deg, rgba(${hexToRgb(color)},0.2), rgba(${hexToRgb(color)},0.05))`,
              border: `2px solid ${color}`,
              borderRadius: 4,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: `0 0 30px rgba(${hexToRgb(color)},0.3), 0 0 60px rgba(${hexToRgb(color)},0.1)`,
              clipPath: 'polygon(12px 0%, 100% 0%, 100% calc(100% - 12px), calc(100% - 12px) 100%, 0% 100%, 0% 12px)',
              position: 'relative',
              flexShrink: 0,
            }}>
              {!employee.photoUrl && (
                <span style={{
                  fontFamily: 'Orbitron, monospace',
                  fontWeight: 900,
                  fontSize: '2.5rem',
                  color: color,
                  textShadow: `0 0 20px ${color}`,
                }}>
                  {initials}
                </span>
              )}
              {/* Status indicator */}
              <div style={{
                position: 'absolute',
                bottom: 8, right: 8,
                width: 10, height: 10,
                borderRadius: '50%',
                background: '#00FF88',
                boxShadow: '0 0 8px rgba(0,255,136,0.8)',
                animation: 'pulseDot 2s ease-in-out infinite',
              }} />
            </div>

            {/* Dept badge */}
            <div style={{
              padding: '4px 12px',
              background: `rgba(${hexToRgb(color)},0.1)`,
              border: `1px solid rgba(${hexToRgb(color)},0.4)`,
              borderRadius: 2,
              fontFamily: 'Share Tech Mono, monospace',
              fontSize: '0.55rem',
              letterSpacing: '0.15em',
              color: color,
              textTransform: 'uppercase',
              textAlign: 'center',
            }}>
              {deptName}
            </div>

            {/* Radar chart */}
            <div style={{ textAlign: 'center' }}>
              <div style={{
                fontFamily: 'Share Tech Mono, monospace',
                fontSize: '0.5rem',
                letterSpacing: '0.2em',
                color: 'rgba(232,244,255,0.4)',
                textTransform: 'uppercase',
                marginBottom: '0.5rem',
              }}>
                CAPABILITY MATRIX
              </div>
              <RadarChart skills={skills} color={color} />
            </div>
          </div>

          {/* Right column: info */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
            {/* Name & role */}
            <div>
              <h2 style={{
                fontFamily: 'Orbitron, monospace',
                fontWeight: 900,
                fontSize: 'clamp(1.2rem, 2.5vw, 1.8rem)',
                color: '#E8F4FF',
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                margin: '0 0 0.25rem 0',
              }}>
                {employee.name}
              </h2>
              <div style={{
                fontFamily: 'Share Tech Mono, monospace',
                fontSize: '0.8rem',
                color: color,
                letterSpacing: '0.1em',
                textShadow: `0 0 8px rgba(${hexToRgb(color)},0.5)`,
              }}>
                {employee.roleTitle}
              </div>
            </div>

            {/* Status row */}
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
              {[
                { label: 'STATUS', value: 'AVAILABLE', icon: <Zap size={10} />, color: '#00FF88' },
                { label: 'CLEARANCE', value: 'LEVEL 5', icon: <Shield size={10} />, color: color },
                { label: 'EFFICIENCY', value: '99.7%', icon: <TrendingUp size={10} />, color: '#FFB800' },
                { label: 'RATING', value: '★★★★★', icon: <Star size={10} />, color: '#FFB800' },
              ].map(stat => (
                <div key={stat.label} style={{
                  padding: '6px 12px',
                  background: 'rgba(13,17,35,0.8)',
                  border: `1px solid rgba(${hexToRgb(stat.color)},0.3)`,
                  borderRadius: 3,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '2px',
                }}>
                  <div style={{
                    fontFamily: 'Share Tech Mono, monospace',
                    fontSize: '0.45rem',
                    letterSpacing: '0.2em',
                    color: 'rgba(232,244,255,0.4)',
                    textTransform: 'uppercase',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '3px',
                  }}>
                    <span style={{ color: stat.color }}>{stat.icon}</span>
                    {stat.label}
                  </div>
                  <div style={{
                    fontFamily: 'Orbitron, monospace',
                    fontWeight: 700,
                    fontSize: '0.6rem',
                    color: stat.color,
                    letterSpacing: '0.1em',
                  }}>
                    {stat.value}
                  </div>
                </div>
              ))}
            </div>

            {/* Bio */}
            <div style={{
              padding: '1rem',
              background: 'rgba(0,0,0,0.3)',
              border: '1px solid rgba(0,166,235,0.1)',
              borderLeft: `3px solid ${color}`,
              borderRadius: '0 4px 4px 0',
            }}>
              <div style={{
                fontFamily: 'Share Tech Mono, monospace',
                fontSize: '0.5rem',
                letterSpacing: '0.2em',
                color: 'rgba(232,244,255,0.4)',
                textTransform: 'uppercase',
                marginBottom: '0.5rem',
              }}>
                AGENT PROFILE
              </div>
              <p style={{
                fontFamily: 'Rajdhani, sans-serif',
                fontSize: '0.85rem',
                lineHeight: 1.6,
                color: 'rgba(232,244,255,0.8)',
                margin: 0,
              }}>
                {employee.bio || `${employee.name} is a highly specialized AI agent engineered for peak performance in ${deptName}. Deployed across enterprise environments, this agent delivers measurable ROI from day one — no training, no onboarding delays, no HR overhead.`}
              </p>
            </div>

            {/* Value proposition */}
            {employee.valueProposition && (
              <div style={{
                padding: '0.75rem 1rem',
                background: `rgba(${hexToRgb(color)},0.05)`,
                border: `1px solid rgba(${hexToRgb(color)},0.2)`,
                borderRadius: 4,
              }}>
                <div style={{
                  fontFamily: 'Share Tech Mono, monospace',
                  fontSize: '0.5rem',
                  letterSpacing: '0.2em',
                  color: color,
                  textTransform: 'uppercase',
                  marginBottom: '0.4rem',
                }}>
                  ◈ WHY DEPLOY THIS AGENT
                </div>
                <p style={{
                  fontFamily: 'Rajdhani, sans-serif',
                  fontSize: '0.8rem',
                  lineHeight: 1.5,
                  color: 'rgba(232,244,255,0.7)',
                  margin: 0,
                }}>
                  {employee.valueProposition}
                </p>
              </div>
            )}

            {/* Skills */}
            <div>
              <div style={{
                fontFamily: 'Share Tech Mono, monospace',
                fontSize: '0.5rem',
                letterSpacing: '0.2em',
                color: 'rgba(232,244,255,0.4)',
                textTransform: 'uppercase',
                marginBottom: '0.5rem',
              }}>
                CORE CAPABILITIES
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {skills.map((skill, i) => (
                  <span key={i} style={{
                    fontFamily: 'Share Tech Mono, monospace',
                    fontSize: '0.55rem',
                    letterSpacing: '0.08em',
                    color: color,
                    background: `rgba(${hexToRgb(color)},0.08)`,
                    border: `1px solid rgba(${hexToRgb(color)},0.25)`,
                    padding: '3px 8px',
                    borderRadius: 2,
                    textTransform: 'uppercase',
                    clipPath: 'polygon(4px 0%, 100% 0%, calc(100% - 4px) 100%, 0% 100%)',
                  }}>
                    {skill}
                  </span>
                ))}
              </div>
            </div>

            {/* Compatible harnesses */}
            <div>
              <div style={{
                fontFamily: 'Share Tech Mono, monospace',
                fontSize: '0.5rem',
                letterSpacing: '0.2em',
                color: 'rgba(232,244,255,0.4)',
                textTransform: 'uppercase',
                marginBottom: '0.5rem',
              }}>
                COMPATIBLE HARNESSES
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {harnesses.map((h, i) => (
                  <span key={i} style={{
                    fontFamily: 'Orbitron, monospace',
                    fontWeight: 600,
                    fontSize: '0.5rem',
                    letterSpacing: '0.1em',
                    color: '#FFB800',
                    background: 'rgba(255,184,0,0.08)',
                    border: '1px solid rgba(255,184,0,0.25)',
                    padding: '3px 8px',
                    borderRadius: 2,
                    textTransform: 'uppercase',
                  }}>
                    ⬡ {h}
                  </span>
                ))}
              </div>
            </div>

            {/* Price + CTA */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '1rem',
              background: 'rgba(0,0,0,0.4)',
              border: `1px solid rgba(${hexToRgb(color)},0.3)`,
              borderRadius: 4,
              marginTop: '0.5rem',
            }}>
              <div>
                <div style={{
                  fontFamily: 'Share Tech Mono, monospace',
                  fontSize: '0.5rem',
                  letterSpacing: '0.2em',
                  color: 'rgba(232,244,255,0.4)',
                  textTransform: 'uppercase',
                  marginBottom: '4px',
                }}>
                  DEPLOYMENT PACKAGE
                </div>
                <div style={{
                  fontFamily: 'Orbitron, monospace',
                  fontWeight: 900,
                  fontSize: '2rem',
                  color: '#FFB800',
                  textShadow: '0 0 20px rgba(255,184,0,0.4)',
                  lineHeight: 1,
                }}>
                  ${price.toLocaleString()}
                </div>
                <div style={{
                  fontFamily: 'Share Tech Mono, monospace',
                  fontSize: '0.5rem',
                  color: 'rgba(232,244,255,0.3)',
                  letterSpacing: '0.1em',
                  marginTop: '2px',
                }}>
                  ONE-TIME · LIFETIME ACCESS · NO MONTHLY FEES
                </div>
              </div>

              <button
                onClick={handleAddToCart}
                style={{
                  background: `linear-gradient(135deg, rgba(${hexToRgb(color)},0.2), rgba(${hexToRgb(color)},0.1))`,
                  border: `1px solid ${color}`,
                  borderRadius: 4,
                  padding: '0.75rem 1.5rem',
                  cursor: 'pointer',
                  color: color,
                  fontFamily: 'Orbitron, monospace',
                  fontWeight: 700,
                  fontSize: '0.7rem',
                  letterSpacing: '0.15em',
                  textTransform: 'uppercase',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.2s ease',
                  boxShadow: `0 0 20px rgba(${hexToRgb(color)},0.2)`,
                  clipPath: 'polygon(8px 0%, 100% 0%, calc(100% - 8px) 100%, 0% 100%)',
                }}
                onMouseEnter={e => {
                  (e.currentTarget as HTMLElement).style.background = `rgba(${hexToRgb(color)},0.3)`;
                  (e.currentTarget as HTMLElement).style.boxShadow = `0 0 30px rgba(${hexToRgb(color)},0.4)`;
                  (e.currentTarget as HTMLElement).style.transform = 'scale(1.02)';
                }}
                onMouseLeave={e => {
                  (e.currentTarget as HTMLElement).style.background = `linear-gradient(135deg, rgba(${hexToRgb(color)},0.2), rgba(${hexToRgb(color)},0.1))`;
                  (e.currentTarget as HTMLElement).style.boxShadow = `0 0 20px rgba(${hexToRgb(color)},0.2)`;
                  (e.currentTarget as HTMLElement).style.transform = 'scale(1)';
                }}
              >
                <ShoppingCart size={16} />
                DEPLOY AGENT
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
